import { useState, useEffect, useRef } from "react";
import { Printer, Settings, ChevronDown, ChevronUp, Search, Check, Filter, Info } from "lucide-react";
import { useAdminOrders } from "../hooks/useAdminOrder";
import { Order } from "../types/order";

const LOCAL_STORAGE_KEY = "label_from_address";

const STATUS_OPTIONS = [
  { label: "Confirmed", value: "CONFIRMED" },
  { label: "Shipped", value: "SHIPPED" },
  { label: "Pending", value: "PENDING" },
  { label: "Delivered", value: "DELIVERED" },
  { label: "New", value: "NEW" },
  { label: "Cancelled", value: "CANCELLED" },
];

const StatusDropdown = ({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (val: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const label =
    selected.length === 0
      ? "All Statuses"
      : selected.length === STATUS_OPTIONS.length
        ? "All Statuses"
        : selected.length === 1
          ? STATUS_OPTIONS.find((o) => o.value === selected[0])?.label ?? selected[0]
          : `${selected.length} statuses`;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 pl-3 pr-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 hover:border-gray-400 transition-colors min-w-[140px]"
      >
        <Filter className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        <span className="flex-1 text-left truncate">{label}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1 overflow-hidden">
          {/* All option */}
          <button
            onClick={() => onChange([])}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors"
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected.length === 0 ? "bg-blue-600 border-blue-600" : "border-gray-300"
              }`}>
              {selected.length === 0 && <Check className="h-3 w-3 text-white" />}
            </div>
            <span className="text-gray-700">All Statuses</span>
          </button>

          <div className="border-t border-gray-100 my-1" />

          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => toggle(opt.value)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-gray-50 transition-colors"
            >
              <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected.includes(opt.value) ? "bg-blue-600 border-blue-600" : "border-gray-300"
                }`}>
                {selected.includes(opt.value) && <Check className="h-3 w-3 text-white" />}
              </div>
              <span className="text-gray-700">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface FromAddress {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  pincode: string;
  phone: string;
}

const DEFAULT_FROM: FromAddress = {
  name: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  pincode: "",
  phone: "",
};

const LabelCell = ({ order, from }: { order: Order; from: FromAddress }) => {
  const addr = order.shippingAddress;
  const addressLines = [addr.line1, addr.line2, addr.line3].filter(Boolean);

  return (
    <div className="label-cell">
      {order.items?.length > 0 && (
        <div className="label-section label-items">
          <div className="label-heading">ITEMS:</div>
          {order.items.map((item, i) => {
            const variants = Object.entries(item.attributes ?? {});
            return (
              <div key={i} className="label-item-row">
                <span>{item.title}</span>
                {variants.length > 0 && (
                  <span className="label-item-variant">
                    {" "}({variants.map(([k, v]) => `${k}: ${v}`).join(", ")})
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="label-section">
        <div className="label-heading">TO:</div>
        <div className="label-name">{addr.name}</div>
        <div>{addr.mobile}</div>
        {addressLines.map((line, i) => (
          <div key={i}>{line}</div>
        ))}
        <div>
          {addr.district}, {addr.state} - {addr.pincode}
        </div>
      </div>

      <div className="label-section label-from">
        <div className="label-heading">FROM:</div>
        <div className="label-name">{from.name}</div>
        {from.line1 && <div>{from.line1}</div>}
        {from.line2 && <div>{from.line2}</div>}
        <div>
          {from.city}
          {from.state ? `, ${from.state}` : ""}
          {from.pincode ? ` - ${from.pincode}` : ""}
        </div>
        {from.phone && <div>{from.phone}</div>}
      </div>
    </div>
  );
};

const PrintStyles = ({ format }: { format: 'A4' | '100x150' }) => (
  <style>{`
    .screen-hidden { display: none; }

    @media print {
      @page {
        margin: ${format === 'A4' ? '10mm' : '2mm'};
        size: ${format === 'A4' ? 'A4 portrait' : '100mm 150mm portrait'};
      }
      .screen-hidden { display: block !important; }
      body * { visibility: hidden !important; }
      #print-area { visibility: visible !important; position: absolute; top: 0; left: 0; width: 100%; }
      #print-area * { visibility: visible !important; }
      ${format === 'A4' ? `
      .label-page {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 120mm 120mm;
        width: 100%;
        page-break-after: always;
        break-after: page;
      }
      .label-page:last-child { page-break-after: avoid; break-after: avoid; }
      .label-cell {
        border: 1px solid #000;
        padding: 16px;
        font-size: 11pt;
        font-family: Arial, sans-serif;
        overflow: hidden;
        box-sizing: border-box;
        height: 120mm;
        max-height: 120mm;
      }
      ` : `
      .label-page {
        display: block;
        width: 100%;
        height: 100%;
        page-break-after: always;
        break-after: page;
      }
      .label-page:last-child { page-break-after: avoid; break-after: avoid; }
      .label-cell {
        border: 1px solid #000;
        padding: 12px;
        font-size: 11pt;
        font-family: Arial, sans-serif;
        overflow: hidden;
        box-sizing: border-box;
        height: 146mm;
        max-height: 146mm;
      }
      `}
      .label-section { margin-bottom: 10px; }
      .label-items { margin-bottom: 10px; }
      .label-from { margin-bottom: 0; }
      .label-heading { font-weight: bold; margin-bottom: 3px; font-size: 10pt; }
      .label-name { font-weight: 600; }
      .label-item-row { font-size: 9.5pt; line-height: 1.4; }
      .label-item-variant { color: #555; font-size: 9pt; }
    }

    @media screen {
      .label-page {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0;
        border: 1px solid #e5e7eb;
        margin-bottom: 8px;
      }
      .label-cell {
        border: 1px solid #d1d5db;
        padding: 16px;
        font-size: 13px;
        min-height: 180px;
        background: white;
      }
      .label-section { margin-bottom: 10px; }
      .label-from { margin-bottom: 0; }
      .label-heading { font-weight: 700; margin-bottom: 3px; font-size: 13px; }
      .label-name { font-weight: 600; }
    }
  `}</style>
);

const LabelDownload = () => {
  const [from, setFrom] = useState<FromAddress>(() => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      return stored ? JSON.parse(stored) : DEFAULT_FROM;
    } catch {
      return DEFAULT_FROM;
    }
  });
  const [fromEditorOpen, setFromEditorOpen] = useState(!from.name);
  const [draftFrom, setDraftFrom] = useState<FromAddress>(from);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [statusFilters, setStatusFilters] = useState<string[]>(["CONFIRMED"]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [printFormat, setPrintFormat] = useState<'A4' | '100x150'>('A4');

  // When exactly 1 status is selected, pass it to the API so the limit
  // applies only to that status — avoids missing orders due to mixed-status pagination.
  // For multi-select or "all", fetch ALL and filter client-side with a higher limit.
  const apiStatus = statusFilters.length === 1 ? statusFilters[0] : "ALL";
  const apiLimit = statusFilters.length === 1 ? 500 : 200;

  // Add 1 day to dateTo so the full selected day is included
  const adjustedDateTo = dateTo
    ? (() => { const d = new Date(dateTo); d.setDate(d.getDate() + 1); return d.toISOString().split("T")[0]; })()
    : undefined;

  const { data, isLoading } = useAdminOrders(apiStatus, {
    page: 1,
    limit: apiLimit,
    search: search || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: adjustedDateTo,
  });

  const orders: Order[] = (data?.orders ?? [])
    .filter((o) => statusFilters.length === 0 || statusFilters.includes(o.status))
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const selectedOrders = orders.filter((o) => selectedIds.has(o._id));

  const saveFrom = () => {
    setFrom(draftFrom);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(draftFrom));
    setFromEditorOpen(false);
  };

  const toggleOrder = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (orders.every((o) => selectedIds.has(o._id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        orders.forEach((o) => next.delete(o._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        orders.forEach((o) => next.add(o._id));
        return next;
      });
    }
  };

  const handlePrint = () => {
    if (!from.name) {
      alert("Please set your From address before printing.");
      setFromEditorOpen(true);
      return;
    }
    if (selectedOrders.length === 0) {
      alert("Please select at least one order.");
      return;
    }
    const date = new Date().toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).replace(/ /g, "-");
    const prevTitle = document.title;
    document.title = `Shipping-Labels_${date}_${selectedOrders.length}-orders`;
    window.print();
    document.title = prevTitle;
  };

  const allOnPageSelected =
    orders.length > 0 && orders.every((o) => selectedIds.has(o._id));

  return (
    <>
      <PrintStyles format={printFormat} />

      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Shipping Labels</h1>
              <p className="text-sm text-gray-500 mt-0.5">Select orders and print shipping labels</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={printFormat}
                onChange={(e) => setPrintFormat(e.target.value as 'A4' | '100x150')}
                className="bg-white border border-gray-300 text-gray-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block px-3 py-2 outline-none"
              >
                <option value="A4">A4 (4 labels/page)</option>
                <option value="100x150">100x150mm (1 label/page)</option>
              </select>
              <button
                onClick={handlePrint}
                disabled={selectedOrders.length === 0}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0"
              >
                <Printer className="h-4 w-4" />
                <span>{selectedOrders.length > 0 ? `Print (${selectedOrders.length})` : "Print"}</span>
              </button>
            </div>
          </div>

          {/* Print tip */}
          <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              <span className="font-semibold">Before printing:</span> In the browser print dialog, turn off{" "}
              <span className="font-semibold">"Headers and footers"</span> to avoid the URL and page numbers appearing on labels, and labels splitting across pages.
            </p>
          </div>

          {/* From Address */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => setFromEditorOpen((v) => !v)}
              className="w-full flex items-center justify-between p-4 sm:p-6 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Settings className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">From Address (Sender)</p>
                  {from.name ? (
                    <p className="text-xs text-gray-500 mt-0.5">{from.name}{from.city ? `, ${from.city}` : ""}</p>
                  ) : (
                    <p className="text-xs text-amber-600 mt-0.5">Not set — required before printing</p>
                  )}
                </div>
              </div>
              {fromEditorOpen ? (
                <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
            </button>

            {fromEditorOpen && (
              <div className="px-4 sm:px-6 pb-6 border-t border-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  {(
                    [
                      ["name", "Store / Sender Name*"],
                      ["phone", "Phone*"],
                      ["line1", "Address Line 1"],
                      ["line2", "Address Line 2"],
                      ["city", "City"],
                      ["state", "State"],
                      ["pincode", "Pincode"],
                    ] as [keyof FromAddress, string][]
                  ).map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                      <input
                        type="text"
                        value={draftFrom[key]}
                        onChange={(e) => setDraftFrom((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={saveFrom}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setDraftFrom(from); setFromEditorOpen(false); }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Order Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-100 space-y-3">
              <div className="flex items-center justify-between">
                <p className="font-medium text-gray-900 text-sm">
                  Select Orders
                  {selectedOrders.length > 0 && (
                    <span className="ml-2 text-xs text-blue-600 font-medium">{selectedOrders.length} selected</span>
                  )}
                </p>
              </div>
              {/* Row 1: Date range */}
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                />
                <span className="text-gray-400 text-xs flex-shrink-0">to</span>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="flex-1 py-2 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-700"
                />
                {(dateFrom || dateTo) && (
                  <button
                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                    className="flex-shrink-0 text-xs text-gray-400 hover:text-gray-600 underline"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Row 2: Status + Search */}
              <div className="flex items-center gap-2">
                <StatusDropdown
                  selected={statusFilters}
                  onChange={(val) => { setStatusFilters(val); }}
                />
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); }}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                Loading orders...
              </div>
            ) : orders.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-gray-400 text-sm">
                No orders found
              </div>
            ) : (
              <>
                {/* Select-all bar */}
                <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center gap-3 bg-gray-50">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleAll}
                    className="rounded"
                  />
                  <span className="text-xs text-gray-500">Select all on this page</span>
                </div>

                {/* Order list */}
                <div className="divide-y divide-gray-100">
                  {orders.map((order) => {
                    const addr = order.shippingAddress;
                    const isSelected = selectedIds.has(order._id);
                    return (
                      <div
                        key={order._id}
                        onClick={() => toggleOrder(order._id)}
                        className={`flex items-start gap-3 px-4 sm:px-6 py-4 cursor-pointer transition-colors ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                          }`}
                      >
                        <div className="pt-0.5 flex-shrink-0">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                            }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-gray-900 text-sm truncate">{addr.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 flex-shrink-0">
                              {order.status}
                            </span>
                          </div>

                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="font-mono text-xs text-gray-400">{order.orderId}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs text-gray-400">
                              {new Date(order.createdAt).toLocaleDateString("en-IN", {
                                day: "numeric", month: "short", year: "numeric",
                              })}
                            </span>
                          </div>

                          <div className="text-xs text-gray-500 mt-0.5 truncate">
                            {addr.mobile}{addr.district ? ` · ${addr.district}, ${addr.state}` : ""}
                          </div>

                          {order.items?.length > 0 && (
                            <div className="mt-2 space-y-0.5">
                              {order.items.slice(0, 2).map((item) => (
                                <div key={item.orderItemId} className="flex items-center gap-1 text-xs text-gray-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                                  <span className="truncate">{item.title}</span>
                                  {Object.keys(item.attributes ?? {}).length > 0 && (
                                    <span className="text-gray-400 flex-shrink-0">({Object.values(item.attributes).join(", ")})</span>
                                  )}
                                  <span className="text-gray-400 flex-shrink-0">×{item.quantity}</span>
                                </div>
                              ))}
                              {order.items.length > 2 && (
                                <div className="text-xs text-gray-400 pl-2.5">+{order.items.length - 2} more</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {orders.length === 100 && (
                  <div className="px-4 sm:px-6 py-3 border-t border-gray-100 text-xs text-gray-400">
                    Showing latest 100 orders. Use search to find older ones.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hidden on screen, visible only during print */}
          <div id="print-area" className="screen-hidden">
            {printFormat === 'A4' ? (
              Array.from({ length: Math.ceil(selectedOrders.length / 4) }, (_, i) =>
                selectedOrders.slice(i * 4, i * 4 + 4)
              ).map((pageOrders, i) => (
                <div key={i} className="label-page">
                  {pageOrders.map((order) => (
                    <LabelCell key={order._id} order={order} from={from} />
                  ))}
                </div>
              ))
            ) : (
              selectedOrders.map((order, i) => (
                <div key={i} className="label-page">
                  <LabelCell key={order._id} order={order} from={from} />
                </div>
              ))
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default LabelDownload;
